<template>
  <main class="auth-form-page">
    <AppNavBar :border="false" />

    <section class="auth-form-head">
      <h1>{{ t('auth.setWalletPassword') }}</h1>
      <p>{{ pageTip }}</p>
    </section>

    <section class="auth-form-section">
      <label class="auth-form-label">
        {{ t('auth.strongPassword') }} <em>*</em>
      </label>
      <van-field
        v-model="password"
        class="auth-form-field"
        :type="showPassword ? 'text' : 'password'"
        clearable
        :placeholder="t('auth.enterCreateWalletPassword')"
      >
        <template #button>
          <button type="button" class="auth-eye-btn" @click="showPassword = !showPassword">
            <app-image :src="showPassword ? eyeOpenIcon : eyeCloseIcon" alt="" />
          </button>
        </template>
      </van-field>
      <div v-if="password" class="strength-row" :class="`strength-row--${strengthLevel}`">
        <div class="strength-row-line" v-show="password.length > 7">
          <span v-for="item in 3" :key="item" :class="{ active: item <= strengthLevel }" />
        </div>
        <div data-strong :class="`level-${strengthLevel} ${password.length < 8 ? 'red' : ''}`">{{ strengthText }}</div>
      </div>
    </section>

    <section class="auth-form-section">
      <label class="auth-form-label">
        {{ t('auth.confirmPassword') }} <em>*</em>
      </label>
      <van-field
        v-model="passwordAgain"
        class="auth-form-field"
        :class="{ 'auth-form-field--error': passwordAgain && passwordAgain !== password }"
        :type="showPasswordAgain ? 'text' : 'password'"
        clearable
        :placeholder="t('auth.enterCreateWalletPassword')"
      >
        <template #button>
          <button type="button" class="auth-eye-btn" @click="showPasswordAgain = !showPasswordAgain">
            <app-image :src="showPasswordAgain ? eyeOpenIcon : eyeCloseIcon" alt="" />
          </button>
        </template>
      </van-field>
      <div v-if="passwordAgain" class="strength-row" :class="`strength-row--${strengthLevelAgain}`">
        <div class="strength-row-line" v-show="passwordAgain.length > 7">
          <span v-for="item in 3" :key="item" :class="{ active: item <= strengthLevelAgain }" />
        </div>
        <div data-strong :class="`level-${strengthLevelAgain} ${passwordAgain.length < 8 ? 'red' : ''}`">{{ strengthTextAgain }}</div>
      </div>
    </section>

    <section v-if="showEmailSection" class="auth-form-section">
      <label class="auth-form-label">
        {{ t('auth.bindEmail') }} <em>*</em>
        <small>{{ t('auth.emailForRecovery') }}</small>
      </label>
      <van-field v-model="email" class="auth-form-field mt-20" clearable type="email" :placeholder="t('auth.enterEmail')" />
      <van-field
        v-model="emailCode"
        class="auth-form-field"
        clearable
        maxlength="8"
        :placeholder="t('auth.emailCode')"
      >
        <template #button>
          <button type="button" class="auth-code-btn" :disabled="countdown > 0" @click="sendBindCode">
            {{ countdown > 0 ? `${t('auth.resend')}${countdown}s` : t('auth.getCode') }}
          </button>
        </template>
      </van-field>
      <p v-if="countdown > 0" class="code-tip">{{ t('auth.codeSentValid') }}</p>
    </section>

    <section class="auth-form-section">
      <label class="auth-form-label">{{ t('auth.invitationCode') }}</label>
      <van-field v-model="invitationCode" class="auth-form-field" clearable :placeholder="t('auth.enterInvitationCode')">
        <template #button>
          <button type="button" class="auth-scan-btn">
            <app-image  src="@/assets/images/common_copy_icon_gray.png" alt="" />
          </button>
          <button type="button" class="auth-scan-btn" @click="openScanner">
            <app-image src="@/assets/images/common_scancode_icon3.png" alt="" />
          </button>
        </template>
      </van-field>
    </section>

    <AuthThemeButton block class="auth-submit-btn" :disabled="passwordDisabled" :loading="submitting" @click="submitWallet">
      {{ submitText }}
    </AuthThemeButton>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  addAddress,
  devicesBindEmail,
  devicesBindEmailSend,
  emailRegister,
  startWebxBinding,
  userCheckInvitationCode,
  userGetUserInfoByAddress
} from '@/api/auth';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { useProfileStore } from '@/stores/profile';
import { getDeviceId } from '@/utils/device';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import {
  clearTempImportSecret,
  getTempImportSecret,
  saveWalletMnemonic,
  saveWalletPassword,
  saveWalletSignerSecret
} from '@/utils/walletSession';
import { useWalletScanner } from '@/composables/useWalletScanner';
import { deriveAccountAddresses } from '@/utils/walletAddress';
import eyeOpenIcon from '@/assets/images/eye_open_icon.png';
import eyeCloseIcon from '@/assets/images/eye_close_icon.png';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { openWalletScanner } = useWalletScanner();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, 'auth-create-wallet', {}));

const type = computed(() => pageState.value.type || 'new');
const password = ref('');
const passwordAgain = ref('');
const email = ref(String(pageState.value.email || ''));
const emailCode = ref(String(pageState.value.code || ''));
const invitationCode = ref(String(pageState.value.invitationCode || ''));
const showPassword = ref(false);
const showPasswordAgain = ref(false);
const submitting = ref(false);
const countdown = ref(0);
let timer = null;

const isEmailCreateFlow = computed(() => type.value === 'new' && !!String(pageState.value.email || '').trim() && !!String(pageState.value.code || '').trim());
const showEmailSection = computed(() => !isEmailCreateFlow.value && type.value !== 'import');
const passwordDisabled = computed(() => !password.value || !passwordAgain.value);

const pageTip = computed(() => (
  type.value === 'emailIn'
    ? t('auth.setPasswordForTransaction')
    : t('auth.walletPasswordTip')
));
const submitText = computed(() => (
  type.value === 'new' || type.value === 'emailIn'
    ? t('auth.createWallet')
    : t('auth.importWallet')
));
const flowWalletType = computed(() => {
  if (isEmailCreateFlow.value) {
    return 'createEmail';
  }
  if (type.value === 'import') {
    return 'rememberWords';
  }
  return 'createWallet';
});

const strengthLevel = computed(() => {
  const value = password.value;
  if (value.length < 8) return 0;
  let count = 0;
  if (/[A-Z]/.test(value)) count += 1;
  if (/[a-z]/.test(value)) count += 1;
  if (/\d/.test(value)) count += 1;
  if (/[!@#$&*~^%()\-_+=.,;:]/.test(value)) count += 1;
  if (value.length >= 12 && count === 4) return 3;
  if (value.length >= 12 && count >= 3) return 2;
  return 1;
});
const strengthText = computed(() => {
  if (strengthLevel.value === 3) return t('auth.passwordStrong');
  if (strengthLevel.value === 2) return t('auth.passwordMedium');
  if (strengthLevel.value === 1) return t('auth.passwordWeak');
  return t('auth.passwordTooShort');
});

const strengthLevelAgain = computed(() => {
  const value = passwordAgain.value;
  if (value.length < 8) return 0;
  let count = 0;
  if (/[A-Z]/.test(value)) count += 1;
  if (/[a-z]/.test(value)) count += 1;
  if (/\d/.test(value)) count += 1;
  if (/[!@#$&*~^%()\-_+=.,;:]/.test(value)) count += 1;
  if (value.length >= 12 && count === 4) return 3;
  if (value.length >= 12 && count >= 3) return 2;
  return 1;
});
const strengthTextAgain = computed(() => {
  if (strengthLevelAgain.value === 3) return t('auth.passwordStrong');
  if (strengthLevelAgain.value === 2) return t('auth.passwordMedium');
  if (strengthLevelAgain.value === 1) return t('auth.passwordWeak');
  return t('auth.passwordTooShort');
});


function startCountdown() {
  countdown.value = 60;
  window.clearInterval(timer);
  timer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      window.clearInterval(timer);
      timer = null;
    }
  }, 1000);
}

function emailValid() {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value);
}

function generateRandomName() {
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  const length = Math.floor(Math.random() * 6) + 3;
  let result = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  for (let i = 1; i < length; i += 1) {
    result += letters[Math.floor(Math.random() * letters.length)];
  }
  return result;
}

async function sendBindCode() {
  if (!emailValid()) {
    showFailToast(t('auth.emailRequired'));
    return;
  }

  try {
    const result = await devicesBindEmailSend({ token: getDeviceId(), email: email.value.trim() });
    if (!result && result !== true) {
      return;
    }
    showPassedToast(t('auth.codeSent'));
    startCountdown();
  } catch (error) {
  }
}

function openScanner() {
  openWalletScanner({
    back: route.path,
    stateNamespace: 'auth-create-wallet',
    statePayload: {
      ...pageState.value,
      type: type.value,
      email: email.value
    }
  });
}

async function parseInvitationCode(result) {
  const value = String(result || '').trim();
  if (!value) {
    return '';
  }
  if (value.includes('r=')) {
    return value.split('r=')[1].split('&')[0] || '';
  }
  if (value.includes('&friend')) {
    const targetAddress = value.split('&friend')[0] || '';
    if (!targetAddress) {
      return '';
    }
    const addInfo = await userGetUserInfoByAddress({
      address: targetAddress,
      targetAddress
    });
    return String(addInfo?.invitationCode || '').trim();
  }
  return value.split('&')[0] || '';
}

async function applyScanResult() {
  if (!pageState.value.scanResult) return;
  invitationCode.value = await parseInvitationCode(pageState.value.scanResult);
  navigateWithState(router, route.path, {
    namespace: 'auth-create-wallet',
    replace: true,
    payload: {
      ...pageState.value,
      scanResult: ''
    }
  });
}

async function buildWallet() {
  const { Wallet } = await import('ethers');
  if (pageState.value.importAddress) {
    return {
      address: String(pageState.value.importAddress)
    };
  }
  const wallet = Wallet.createRandom();
  return {
    address: wallet.address,
    mnemonic: wallet.mnemonic?.phrase || ''
  };
}

async function submitWallet() {
  if (password.value.length < 8 || passwordAgain.value.length < 8) {
    showFailToast(t('auth.passwordTooShort'));
    return;
  }
  if (passwordAgain.value !== password.value) {
    showFailToast(t('auth.passwordMismatch'));
    return;
  }
  if (showEmailSection.value && !emailValid()) {
    showFailToast(t('auth.emailRequired'));
    return;
  }
  if (showEmailSection.value && !emailCode.value.trim()) {
    showFailToast(t('auth.codeRequired'));
    return;
  }

  submitting.value = true;
  try {
    if (invitationCode.value.trim()) {
      const result = await userCheckInvitationCode({ invitationCode: invitationCode.value.trim() });
      if (result?.isCorrect !== 1 && result?.isCorrect !== true) {
        throw new Error(result?.msg || t('auth.verificationFailed'));
      }
    }

    const wallet = await buildWallet();
    const nickName = generateRandomName();

    let addResult = null;
    if (isEmailCreateFlow.value) {
      addResult = await emailRegister({
        email: email.value.trim(),
        token: String(pageState.value.token || getDeviceId()),
        code: emailCode.value.trim(),
        address: wallet.address,
        payPwd: password.value,
        nickName,
        invitationCode: invitationCode.value.trim()
      });
      if (!addResult) {
        return;
      }
    } else {
      if (showEmailSection.value) {
        const bindResult = await devicesBindEmail({ token: getDeviceId(), code: emailCode.value.trim() });
        if (!bindResult && bindResult !== true) {
          return;
        }
      }
      try {
        addResult = await addAddress({
          address: wallet.address,
          nickName,
          invitationCode: invitationCode.value.trim(),
          email: showEmailSection.value ? email.value.trim() : '',
          payPwd: password.value
        });
      } catch (error) {
        // best-effort during local wallet creation
      }
    }

    let derivedAccount = {
      address: wallet.address
    };
    if (wallet.mnemonic) {
      saveWalletMnemonic(wallet.address, wallet.mnemonic);
      saveWalletSignerSecret(wallet.address, wallet.mnemonic, 'mnemonic');
      derivedAccount = await deriveAccountAddresses(wallet.mnemonic, 'mnemonic');
    }
    if (type.value === 'import') {
      const importSecret = getTempImportSecret(wallet.address);
      if (importSecret?.secret) {
        if (importSecret.type === 'mnemonic') {
          saveWalletMnemonic(wallet.address, importSecret.secret);
        }
        saveWalletSignerSecret(wallet.address, importSecret.secret, importSecret.type);
        derivedAccount = await deriveAccountAddresses(importSecret.secret, importSecret.type);
      }
      clearTempImportSecret();
    }
    await profileStore.setCurrentAccount({
      ...derivedAccount,
      address: wallet.address,
      name: nickName,
      email: email.value.trim(),
      walletWay: isEmailCreateFlow.value ? 'ByEmail' : 'noByEmail',
      type: '0',
      isBackup: false
    });
    profileStore.setWalletType(flowWalletType.value);
    saveWalletPassword(wallet.address, password.value);
    profileStore.setFirstVisit(true);
    const loginToken = addResult?.token || addResult?.user?.token;
    if (loginToken) {
      profileStore.setToken(loginToken);
    }
    showPassedToast(t('auth.walletReady'));
    if (isEmailCreateFlow.value) {
      try {
        await startWebxBinding();
        return;
      } catch (error) {
        // WebX binding is optional; the newly-created WinPay wallet remains usable.
      }
    }
    await router.replace('/home/index');
  } catch (error) {
  } finally {
    submitting.value = false;
  }
}

onBeforeUnmount(() => {
  window.clearInterval(timer);
});

watch(() => pageState.value.scanResult, applyScanResult);
onMounted(applyScanResult);
</script>

<style scoped lang="scss">
@use '@/styles/auth-form.scss';

.auth-form-section + .auth-form-section {
  margin-top: 20px;
}


.mt-20 {
  margin-bottom: 20px;
}

.strength-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;


  .strength-row-line {
    display: flex;
    gap: 10px;

    span {
      width: 50px;
      height: 5px;
      border-radius: 16px;
      background: #c9cdd4;
    }

    span.active {
      background: #ff7d00;
    }
  }

  [data-strong] {
    // margin-left: auto;
    font-size: 13px;
    font-weight: 500;
  }
  .red {
    color: red;
  }

  .level-2,
  .level-3 {
    color: #07b077;
  }

  .level-1 {
    color: #ff7d00;
  }
}

.strength-row--2 .strength-row-line span.active,
.strength-row--3 .strength-row-line span.active {
  background: #07b077 !important;
}


.code-tip {
  margin: 5px 0 0;
  color: #ef0027;
  font-size: 12px;
}
</style>
